import React from 'react';
import { Typography, Paper, Grid, Divider } from '@mui/material';

const SharpRatioView = ({ sharpRatioTable, visibility }) => {
  if (!visibility.top3Coins) return null;

  const { sharp_ratio_table = [], sharp_ratio_overview = {} } = sharpRatioTable;

  const groupedByCoin = sharp_ratio_table.reduce((acc, item) => {
    const key = ((item && item.coin) || '—').toString();
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <>
      <Typography variant="h6" gutterBottom>
        🏆🥇🥈🥉 Top Coins to Trade
      </Typography>
      {Object.entries(groupedByCoin).length === 0 ?
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
                <Paper style={{ padding: 16, height: '100%' }}>
                  <Typography variant="subtitle1">Market conditions are favoring capital preservation versus entering a trade.</Typography>
                </Paper>
          </Grid>
        </Grid>
      :
        <Grid container spacing={2}>{groupedByCoin.length}
          {Object.entries(groupedByCoin).map(([coin, orders], index) => {
            const normalize = (o) => (o?.order?.action || o?.action || '').toLowerCase();

            // Split orders cleanly
            const buyOrders      = orders.filter(o => normalize(o).includes('buy'));
            const fireSellOrders = orders.filter(o => normalize(o) === 'fire_sell_negative_group');
            const cancelOrders   = orders.filter(o => (o.action || '').toLowerCase() === 'cancel');

            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper style={{ padding: 16, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>{coin}</Typography>

                  {/* 🔥 FIRE SELL NEGATIVE GROUP */}
                  {fireSellOrders.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>🔥 Fire Sell (Negative Group)</Typography>
                      {fireSellOrders.map((item, i) => (
                        <Paper
                          key={`fire-${i}`}
                          style={{ padding: 12, marginBottom: 12, backgroundColor: '#FFECEC', border: '1px solid #F3C2C2' }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            🧾 <strong>Order Type:</strong> {item.order?.action?.toUpperCase()} &nbsp;|&nbsp;
                            🆔 <strong>ID:</strong> {item.order?.id}
                          </Typography>
                          {item.reason && (
                            <Typography variant="body2" color="textSecondary">
                              📝 {item.reason}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </>
                  )}

                  {/* 💰 BUY ORDERS */}
                  {buyOrders.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>📅 Buy Orders</Typography>
                      {buyOrders.map((item, i) => {
                        const onFill = Array.isArray(item?.order?.on_fill) ? item.order.on_fill : [];
                        return (
                          <Paper
                            key={`buy-${i}`}
                            style={{ padding: 12, marginBottom: 12, backgroundColor: '#F0FFF0', border: '1px solid #DDD' }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              🧠 <strong>Strategy:</strong> {(item.action || '').toUpperCase()} &nbsp;|&nbsp;
                              📄 <strong>Order Type:</strong> {(item.order?.action || '').toUpperCase()} &nbsp;|&nbsp;
                              🆔 <strong>ID:</strong> {item.order?.id}
                            </Typography>
                            <Typography variant="body2">
                              📈 Price: <strong>{item.order?.price}</strong>
                            </Typography>
                            {item.order?.price_limit && (
                              <Typography variant="body2">🔒 Price Limit: {item.order.price_limit}</Typography>
                            )}
                            <Typography variant="body2">
                              📦 Qty: {item.order?.expected_qty || '-'} | 💰 Allocation: {item.order?.allocation_usd ? `$${item.order.allocation_usd}` : '-'}
                            </Typography>
                            {item.reason && (
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                <strong>Reason:</strong> {item.reason}
                              </Typography>
                            )}

                            {/* 📑 On Fill (TP/SL) legs */}
                            {onFill.length > 0 && (
                              <>
                                <Divider style={{ margin: '12px 0' }} />
                                <Typography variant="subtitle2" gutterBottom>📑 On Fill TP/SL Orders</Typography>
                                {onFill.map((subOrder, sidx) => (
                                  <Paper
                                    key={`onfill-${sidx}`}
                                    style={{ padding: 8, marginBottom: 8, backgroundColor: '#FAFAFA', border: '1px solid #CCC' }}
                                  >
                                    <Typography variant="body2">
                                      📄 <strong>Order Type:</strong> {(subOrder.action || '').toUpperCase()} &nbsp;|&nbsp;
                                      🆔 {subOrder.id}
                                    </Typography>
                                    <Typography variant="body2">
                                      📈 Price: {subOrder.price} &nbsp;|&nbsp;
                                      📦 Qty: {subOrder.expected_qty ?? '-'} &nbsp;|&nbsp;
                                      💰 Allocation: {subOrder.allocation_usd ? `$${subOrder.allocation_usd}` : '-'}
                                    </Typography>
                                    {subOrder.reason && (
                                      <Typography variant="body2" color="textSecondary">
                                        📝 {subOrder.reason}
                                      </Typography>
                                    )}
                                    {Array.isArray(subOrder.conditions) && subOrder.conditions.length > 0 && (
                                      <>
                                        <Typography variant="body2" style={{ marginTop: 4 }}>
                                          <strong>Conditions:</strong>
                                        </Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                          {subOrder.conditions.map((cond, cidx) => (
                                            <li key={cidx}>
                                              {cond.field} {cond.operator} {cond.value}
                                            </li>
                                          ))}
                                        </ul>
                                      </>
                                    )}
                                  </Paper>
                                ))}
                              </>
                            )}
                          </Paper>
                        );
                      })}
                    </>
                  )}

                  {/* 🧹 CANCEL ORDERS */}
                  {cancelOrders.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>🧹 Cancel Orders</Typography>
                      {cancelOrders.map((item, i) => (
                        <Paper
                          key={`cancel-${i}`}
                          style={{ padding: 12, marginBottom: 12, backgroundColor: '#F7F7F7', border: '1px solid #DDD' }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            📄 <strong>Order Type:</strong> {item.order?.action?.toUpperCase()} &nbsp;|&nbsp;
                            🆔 <strong>ID:</strong> {item.order?.id}
                          </Typography>
                          {item.reason && (
                            <Typography variant="body2" color="textSecondary">
                              📝 {item.reason}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      }

      {/* SHARPE RATIO OVERVIEW */}
      <Typography variant="h6" gutterBottom style={{ marginTop: 32 }}>
        📊 Sharpe Ratio Overview
      </Typography>
      <Grid container spacing={2} direction="column">
        {[
          { key: 'high', label: 'High Sharpe (≥ 0.70)' },
          { key: 'mid', label: 'Mid Sharpe (0.40-0.69)' },
          { key: 'low', label: 'Low Sharpe (< 0.40)' },
          { key: 'stablecoins', label: 'Stablecoins / Cash' },
        ]
          .filter(tier => sharp_ratio_overview[tier.key])
          .map((tier, i) => {
            const data = sharp_ratio_overview[tier.key];
            const toCsv = (v) => Array.isArray(v) ? v.join(', ') : (typeof v === 'string' ? v : '');
            const orDash = (v) => (v ?? '') !== '' ? v : '—';

            return (
              <Grid item xs={12} key={i}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {tier?.label ?? '—'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {orDash(data?.description)}
                  </Typography>

                  <Typography variant="body2">
                    📌 Allocation: {orDash(data?.allocation_percent)}
                  </Typography>

                  <Typography variant="body2">
                    📋 Coins: {toCsv(data?.coins) || '—'}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
      </Grid>
    </>
  );
};

export default SharpRatioView;
